import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Layout,
  Menu,
  Space,
  Button,
  Drawer,
  Modal,
  Form,
  Row,
  Divider,
  Input,
  Alert,
} from "antd";
import { MenuOutlined } from "@ant-design/icons";
import { useLogout } from "../hooks/useLogout";
import { useAuthContext } from "../hooks/useAuthContext";
import { useOrgContext } from "../hooks/useOrgContext";
import { useCaseContext } from "../hooks/useCaseContext";
import logoImage from "../images/chakra_logo.png";
import TraffickStopLogo from "../images/TraffickStop_logo1.png";
import jwtDecode from "jwt-decode";
import NewNotifications from "../newNotifications/NewNotifications";
import { AsyncTypeahead } from "react-bootstrap-typeahead";
import axios from "axios";
import CaseModal from "./CaseModal";

const { Header } = Layout;

function HeaderComponent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useLogout();
  const { user, triggerSessionExpired } = useAuthContext();
  const { organization, dispatch: orgDispatch } = useOrgContext();
  const { dispatch: caseDispatch, caseNumber } = useCaseContext();
  const [selectedKey, setSelectedKey] = useState("home");
  const [displayOrgs, setDisplayOrgs] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Case handling states
  const [userDetails, setUserDetails] = useState(null);
  const [caseModalShow, setCaseModalShow] = useState(false);
  const [typedCaseNumber, setTypedCaseNumber] = useState("");
  const [existingCaseNumber, setExistingCaseNumber] = useState("");
  const [showWarning, setShowWarning] = useState(false);
  const [caseHistory, setCaseHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const typeaheadRef = useRef(null);

  const token = user?.token;
  const decoded = token ? jwtDecode(token) : null;
  const orgIDs = decoded?.organizations || [];
  const userID = decoded?._id;

  const isCodeFour = () => {
    return window.location.href.startsWith("https://app.codefour.ai");
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1317);
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  }, [user, navigate]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.log("Session expired, logging out...");
          triggerSessionExpired();
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [triggerSessionExpired]);

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!token || !userID) return;
  
      try {
        // Fetch user data
        const userResponse = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/user/${userID}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const user = userResponse.data;
  
        // Fetch organizations only once
        const orgsPromises = orgIDs.map((orgID) =>
          axios.get(
            `${process.env.REACT_APP_BASE_URL}/api/organization/${orgID}`,
            { headers: { Authorization: `Bearer ${token}` } }
          )
        );
        const orgsResponses = await Promise.all(orgsPromises);
        const fetchedOrgs = orgsResponses.map((res) => res.data);
  
        // Update state
        setDisplayOrgs(fetchedOrgs);
  
        // Set selected organization
        const targetOrgId = user.selectedOrganization || orgIDs[0];
        if (!user.selectedOrganization) {
          await axios.put(
            `${process.env.REACT_APP_BASE_URL}/api/user/selectOrganization`,
            { userId: userID, selectedOrganizationId: targetOrgId },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
  
        const currentOrg = fetchedOrgs.find((org) => org._id === targetOrgId);
        if (currentOrg) {
          orgDispatch({ type: "SET_ORGANIZATION", payload: currentOrg });
        }
      } catch (error) {
        console.error("Failed to fetch user or organizations:", error);
      }
    };
  
    fetchInitialData();
  }, [token, userID]); // Runs only once when the page loads


const handleOrgSelection = useCallback(
  async (orgId) => {
    if (orgId === selectedOrganization) return;

    const selectedOrg = displayOrgs.find((org) => org._id === orgId);
    if (!selectedOrg) return;

    try {
      // Update local state and context immediately
      setSelectedOrganization(orgId);
      orgDispatch({ type: "SET_ORGANIZATION", payload: selectedOrg });

      // Update the organization on the server
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/user/selectOrganization`,
        { userId: userID, selectedOrganizationId: orgId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error("Failed to select organization:", error);
      // Revert changes if server update fails
      setSelectedOrganization(selectedOrganization);
      if (organization) {
        orgDispatch({ type: "SET_ORGANIZATION", payload: organization });
      }
    }
  },
  [userID, token, displayOrgs, organization, selectedOrganization, orgDispatch]
);


  // Case handling methods
  const handleInvalidTokenError = (error) => {
    if (error.message.includes("Invalid token specified")) {
      logout();
      navigate("/login");
    }
  };

  const getUser = async () => {
    if (!decoded?._id || !user?.token) return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/user/${decoded._id}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      setUserDetails(response.data);
      console.log("User:", response.data);
    } catch (error) {
      console.error("Error:", error);
      handleInvalidTokenError(error);
      if (error.response?.data?.type === "EXPIRED_TOKEN") {
        logout();
      }
    }
  };

  const getCaseHistoryForUser = async () => {
    if (!decoded?._id || !user?.token) return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/case-history/${decoded._id}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      setCaseHistory(response.data);
    } catch (error) {
      console.error("Error:", error);
      if (error.response?.data?.type === "EXPIRED_TOKEN") {
        logout();
      }
    }
  };

  const handleCaseNumberChange = (e) => {
    const newCaseNumber = e.target.value;
    setTypedCaseNumber(newCaseNumber); // Only update local state while typing
    setExistingCaseNumber("");
  };

  const handleExistingCaseNumberChange = (selected) => {
    if (selected.length > 0) {
      const selectedCaseNumber = selected[0].caseNumber;
      setExistingCaseNumber(selectedCaseNumber);
      setTypedCaseNumber("");
      localStorage.setItem("caseNumber", selectedCaseNumber);
      caseDispatch({ type: "SET_CASE_NUMBER", payload: selectedCaseNumber });
    }
  };

  const handleCaseNumberFocus = () => {
    setExistingCaseNumber("");
    typeaheadRef.current?.clear();
  };

  const handleExistingCaseNumberFocus = () => {
    setTypedCaseNumber("");
    handleSearch("");
  };

  const handleSearch = (query) => {
    if (query === "") {
      setCaseHistory(caseHistory.slice(0, 5));
    } else {
      setCaseHistory(
        caseHistory.filter((caseItem) => caseItem.caseNumber.includes(query))
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!typedCaseNumber && !existingCaseNumber) {
      setShowWarning(true);
      setLoading(false);
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/case-history/create/`,
        {
          UserId: decoded._id,
          OrganizationId: organization?._id,
          caseNumber: typedCaseNumber || existingCaseNumber,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      // Only update context and localStorage after successful submission
      localStorage.setItem("caseNumber", typedCaseNumber || existingCaseNumber);
      caseDispatch({
        type: "SET_CASE_NUMBER",
        payload: typedCaseNumber || existingCaseNumber,
      });
      setCaseModalShow(false);
      setShowWarning(false);
    } catch (error) {
      console.error("Error:", error);
      handleInvalidTokenError(error);
    } finally {
      setLoading(false);
    }
  };
  const showCaseModal = async () => {
    setCaseModalShow(true);
    await getCaseHistoryForUser();
  };

  // Additional useEffect for case handling
  useEffect(() => {
    if (!user) {
      navigate("/login");
    } else {
      getUser();
    }
  }, [user]);

  useEffect(() => {
    if (
      location.pathname.startsWith("/search") &&
      user?.RequiresCaseNumber &&
      !caseNumber
    ) {
      setCaseModalShow(true);
      getCaseHistoryForUser();
    }
  }, [location, userDetails, caseNumber, user]);

  const logoutHandler = () => {
    logout();
  };

  const createDropdownLabel = (text) => (
    <Space>
      {text}
      {isMobile ? null : (
        <i
          className="fas fa-angle-down"
          style={{ fontSize: "12px", marginLeft: "4px" }}
        />
      )}
    </Space>
  );

  const applicationsMenu = { // add more apps as needed
    items: [
      ...(decoded?.hasInvestigationsAccess 
        ? [
            {
              key: "investigations",
              label: "Investigations",
              icon: <i className="fas fa-magnifying-glass" />,
              onClick: () => navigate("/investigation"),
            },
          ]
        : []),
      ...(decoded?.hasAgencyDataAccess
        ? [
            {
              key: "dashboards",
              label: "Dashboards",
              icon: <i className="fa-solid fa-display" />,
              onClick: () => navigate("/dashboards"),
            },
          ]
        : []),
    ],
  };
  

  const renderOrganizationsMenu = useMemo(() => {
    if (!orgIDs || orgIDs.length <= 2) return null;

    return {
      key: "organizations",
      icon: <i className="fas fa-people-group" />,
      label: "Organizations",
      children:
        displayOrgs.length === 0
          ? [{ key: "loading", label: "No Organizations" }]
          : displayOrgs.map((org) => ({
              key: org._id,
              label: (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOrgSelection(org._id);
                  }}
                >
                  <Space>
                    {org.agencyName}
                    {org._id === organization?._id && (
                      <i className="fa-solid fa-circle-check" />
                    )}
                  </Space>
                </div>
              ),
            })),
    };
  }, [orgIDs, displayOrgs, selectedOrganization, handleOrgSelection]);


  const menuItems = !isCodeFour() ? [
    {
      key: "home",
      icon: <i className="fas fa-home" style={{ fontSize: "16px" }} />,
      label: "Home",
      onClick: () => navigate("/home"),
    },
    {
      key: "search",
      icon: <i className="fas fa-search" style={{ fontSize: "16px" }} />,
      label: "Search",
      onClick: () => navigate("/search"),
    },
    ...(decoded &&
      (decoded.hasInvestigationsAccess)
      ? [
          {
            key: "applications",
            icon: <i className="fas fa-cube" style={{ fontSize: "16px" }} />,
            label: createDropdownLabel("Applications"),
            children: applicationsMenu.items,
          },
        ]
      : []),
    {
      key: "documentation",
      icon: <i className="fas fa-folder-open" style={{ fontSize: "16px" }} />,
      label: "Documentation",
      onClick: () => navigate("/userdocs"),
    },
    ...(userDetails?.requiresCaseNumber
      ? [
          {
            key: "caseNumber",
            icon: (
              <i className="fas fa-briefcase" style={{ fontSize: "16px" }} />
            ),
            label: caseNumber ? `Case: ${caseNumber}` : "Enter Case Number",
            onClick: showCaseModal,
          },
        ]
      : []),
    renderOrganizationsMenu, // Use the memoized variable here directly
    {
      key: "newNotifications",
      label: isMobile ? (
        <div
          onClick={() => navigate("/notifications")}
          style={{
            marginLeft: "auto",
          }}
        >
          <i className="fas fa-bell" style={{ marginRight: 8 }} />
          <span>Notifications</span>
        </div>
      ) : (
        <NewNotifications />
      ),
      style: {
        display: "flex",
        alignItems: "center", // Vertically centers NewNotifications
        marginLeft: "auto",
      },
    },
    {
      key: "user",
      icon: <i className="fas fa-user" style={{ fontSize: "16px" }} />,
      label: user?.email,
      style: {
      
        color: "var(--bs-theme) !important",
      },
      children: [
        {
          key: "notifications",
          label: "Notifications",
          icon: <i className="fas fa-bell" />,
          onClick: () => navigate("/notifications"),
        },
        {
          key: "cases",
          label: "Case History",
          icon: <i className="fas fa-briefcase" />,
          onClick: () => navigate("/casehistory"),
        },
        {
          key: "settings",
          label: "Settings",
          icon: <i className="fas fa-cog" />,
          onClick: () => navigate("/profile"),
        },
        ...(decoded?.isGlobalAdmin
          ? [
              {
                key: "global",
                label: "Global",
                icon: <i className="fas fa-globe" />,
                onClick: () => navigate("/admin/global/users"),
              },
            ]
          : []),
        ...(decoded?.isLocalAdmin
          ? [
              {
                key: "organizational",
                label: "Organizational",
                icon: <i className="fas fa-people-group" />,
                onClick: () => navigate("/admin/users"),
              },
            ]
          : []),
          ...(decoded?.isIdentityAuditer
            ? [
              {
                key: "optout",
                label: "Opt Out",
                icon: <i className="fas fa-user-slash" />,
                onClick: () => navigate("/optout"),
              },
              ]
            : []),
        {
          key: "logout",
          label: "Logout",
          icon: <i className="fas fa-right-from-bracket" />,
          style: { borderTop: "1px solid rgba(255, 255, 255, 0.1)" },
          onClick: logoutHandler,
        },
      ],
    },
  ].filter(Boolean) : [
    ...(decoded &&
      (decoded.hasInvestigationsAccess)
      ? [
          {
            key: "applications",
            icon: <i className="fas fa-cube" style={{ fontSize: "16px" }} />,
            label: createDropdownLabel("Applications"),
            children: applicationsMenu.items,
          },
        ]
      : []),
      {
        key: "documentation",
        icon: <i className="fas fa-folder-open" style={{ fontSize: "16px" }} />,
        label: "Documentation",
        onClick: () => navigate("/userdocs"),
      },
      ...(decoded &&
        (decoded.hasInvestigationsAccess)
        ? [
             {
        key: "newNotifications",
        label: isMobile ? (
          <div
            onClick={() => navigate("/notifications")}
            style={{
              marginLeft: "auto",
            }}
          >
            <i className="fas fa-bell" style={{ marginRight: 8 }} />
            <span>Notifications</span>
          </div>
        ) : (
          <NewNotifications />
        ),
        style: {
          display: "flex",
          alignItems: "center", // Vertically centers NewNotifications
          marginLeft: "auto",
          marginRight: 220
        },
      },
          ]
        : []),
    {
      key: "user",
      icon: <i className="fas fa-user" style={{ fontSize: "16px", }} />,
      label: user?.email,
      style: {
        float: 'right',
        color: "var(--bs-theme) !important",
        right: 0,
        position: 'absolute',
        marginTop: '5px',
      },
      children: [
        {
          key: "settings",
          label: "Settings",
          icon: <i className="fas fa-cog" />,
          onClick: () => navigate("/profile"),
        },
        {
          key: "logout",
          label: "Logout",
          icon: <i className="fas fa-right-from-bracket" />,
          style: { borderTop: "1px solid rgba(255, 255, 255, 0.1)" },
          onClick: logoutHandler,
        },
      ],
    },
      ];

  const toggleDrawer = () => setDrawerVisible(!drawerVisible);

  const handleLogoClick = () => {
    setSelectedKey("home");
    navigate("/");
  };

  return (
    <>
      <CaseModal
        caseModalShow={caseModalShow}
        setCaseModalShow={setCaseModalShow}
        handleSubmit={handleSubmit}
        typedCaseNumber={typedCaseNumber}
        setTypedCaseNumber={setTypedCaseNumber}
        handleCaseNumberFocus={handleCaseNumberFocus}
        handleCaseNumberChange={handleCaseNumberChange}
        caseHistory={caseHistory}
        handleSearch={handleSearch}
        handleExistingCaseNumberChange={handleExistingCaseNumberChange}
        handleExistingCaseNumberFocus={handleExistingCaseNumberFocus}
        showWarning={showWarning}
        loading={loading}
        existingCaseNumber={existingCaseNumber}
      />

      <Header
        style={{
          position: "fixed",
          width: "100%",
          height: "79px",
          padding: "0 16px",
          background: "rgb(29 40 53)",
          zIndex: 2,
          display: "flex",
          alignItems: "center",
        }}
      >
        <div
          className="logo"
          onClick={handleLogoClick}
          style={{ cursor: "pointer", marginRight: "24px" }}
        >
          {isCodeFour() ? (
            <img src={TraffickStopLogo} alt="Logo" height="50px" />
          ) : (
          <img src={logoImage} alt="Logo" height="50px" />
          )}
        </div>

        {isMobile ? (
  <>
    {isCodeFour() ? (
      // CodeFour mobile view - just email and logout
      <div style={{ marginLeft: 'auto', color: '#fff' }}>
        <Button
          type="text"
          icon={<MenuOutlined />}
          onClick={toggleDrawer}
          style={{ color: "#fff", marginLeft: "auto" }}
        />
        <Drawer
          title={null}
          placement="right"
          onClose={toggleDrawer}
          open={drawerVisible}
          bodyStyle={{ background: "rgb(29, 40, 53)", color: "#fff" }}
        >
          <Menu
            mode="inline"
            theme="dark"
            selectedKeys={[selectedKey]}
            items={[
              ...(decoded &&
                (decoded.hasInvestigationsAccess)
                ? [
                    {
                      key: "applications",
                      icon: <i className="fas fa-cube" style={{ fontSize: "16px" }} />,
                      label: createDropdownLabel("Applications"),
                      children: applicationsMenu.items,
                    },
                  ]
                : []),
                {
                  key: "documentation",
                  icon: <i className="fas fa-folder-open" style={{ fontSize: "16px" }} />,
                  label: "Documentation",
                  onClick: () => navigate("/userdocs"),
                },
                {
                  key: "notifications",
                  label: "Notifications",
                  icon: <i className="fas fa-bell" />,
                  onClick: () => {
                    navigate("/notifications");
                    toggleDrawer();
                  }
                },
              {
                key: "logout",
                label: "Logout",
                icon: <i className="fas fa-right-from-bracket" />,
                onClick: () => {
                  logoutHandler();
                  toggleDrawer();
                }
              },
              {
                key: "user",
                label: user?.email,
                icon: <i className="fas fa-user" style={{ fontSize: "16px" }} />,
              }
            ]}
          />
        </Drawer>
      </div>
    ) : (
      // Original mobile view for non-CodeFour
      <>
        <Button
          type="text"
          icon={<MenuOutlined />}
          onClick={toggleDrawer}
          style={{ color: "#fff", marginLeft: "auto" }}
        />
        <Drawer
          title={null}
          placement="right"
          onClose={toggleDrawer}
          open={drawerVisible}
          bodyStyle={{ background: "rgb(29, 40, 53)", color: "#fff" }}
        >
          <Menu
            mode="inline"
            theme="dark"
            selectedKeys={[selectedKey]}
            items={menuItems.map((item) => ({
              key: item.key,
              label: item.label,
              icon: item.icon,
              children: item.children?.map((child) => ({
                key: child.key,
                label: child.label,
                icon: child.icon,
                onClick: () => {
                  child.onClick?.();
                  setSelectedKey(child.key);
                  toggleDrawer();
                },
              })),
              onClick: item.children
                ? undefined
                : () => {
                    item.onClick?.();
                    setSelectedKey(item.key);
                    toggleDrawer();
                  },
            }))}
          />
        </Drawer>
      </>
    )}
  </>
) : (
  // Original desktop view remains unchanged
  <>
    <Menu
      mode="horizontal"
      theme="dark"
      style={{ flex: 1, background: "transparent", minWidth: 0 }}
      selectedKeys={[selectedKey]}
      items={menuItems}
      onClick={({ key }) => setSelectedKey(key)}
    />
  </>
)}
      </Header>
    </>
  );
}

export default HeaderComponent;
